/* eslint-disable max-len */
import { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, styled, Typography, useMediaQuery, SwipeableDrawer, IconButton } from "@mui/material";
import { LandToken } from "../../types/LandToken";
import BlockLandHeader from "./BlockLandHeader";
import BlockLandPreview from "./BlockLandPreview";
import BlockLandForm from "./BlockLandForm";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { modalTransitionProps, sheetTransitionProps } from "../../components/ScaleTransition";

type ComplaintModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: any) => void;
  loading: boolean;
  error?: string | null;
  land: LandToken | null;
  title?: string;
  infoText?: string;
  confirmLabel?: string;
  showReason?: boolean;
  requireDescription?: boolean;
  complaintDescription?: string;
  fileUrls?: string[];
  fileAliases?: string[];
  isReadOnly?: boolean;
};

export type ComplaintFormData = {
  reason?: string;
  description: string;
  files: File[];
};

const ComplaintModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  error,
  land,
  title = "File Complaint",
  infoText,
  confirmLabel = "File Complaint",
  showReason = true,
  requireDescription = false,
  complaintDescription = "",
  fileUrls = [],
  fileAliases = [],
  isReadOnly = false,
}: ComplaintModalProps) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("md"));
  const MAX_FILES = 5;

  const [reason, setReason] = useState("");
  const [description, setDescription] = useState(complaintDescription);
  const [files, setFiles] = useState<File[]>([]);
  const [pendingFile, setPendingFile] = useState<File | undefined>(undefined);
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeSequenceRef = useRef(0);

  const clearResetTimeout = () => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  };

  const resetModalState = (nextDescription = "") => {
    setReason("");
    setDescription(nextDescription);
    setFiles([]);
    setPendingFile(undefined);
  };

  const handleAddFile = () => {
    if (pendingFile && files.length < MAX_FILES) {
      setFiles([...files, pendingFile]);
      setPendingFile(undefined);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleOpenFile = (url: string) => {
    window.open(url, "_blank");
  };

  const handleDrawerOpen = () => undefined;

  const handleSubmit = () => {
    onConfirm({
      reason,
      description,
      files,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const isFormValid = showReason ? reason.trim().length > 0 : !requireDescription || description.trim().length > 0;

  useEffect(() => {
    if (open) {
      // Invalidate any pending reset from a previous close and keep opened state intact.
      closeSequenceRef.current += 1;
      clearResetTimeout();
      resetModalState(complaintDescription || "");
    }
  }, [open, complaintDescription]);

  useEffect(() => {
    return () => {
      clearResetTimeout();
    };
  }, []);

  useEffect(() => {
    if (open) {
      console.log({
        land,
        complaintDescription,
        fileUrls,
        fileAliases,
        isReadOnly,
      });
    }
  }, [open, land, complaintDescription, fileUrls, fileAliases, isReadOnly]);

  const modalContent = (
    <PopupContainer isMobile={isMobile}>
      <ContentSection isMobile={isMobile}>
        <MainContent>
          <BlockLandHeader onClose={handleClose} title={title} infoText={infoText} />
          <BlockLandPreview land={land} />

          <BlockLandForm
            reason={reason}
            setReason={setReason}
            showReason={showReason}
            description={description}
            setDescription={setDescription}
            files={files}
            MAX_FILES={MAX_FILES}
            pendingFile={pendingFile}
            setPendingFile={setPendingFile}
            handleAddFile={handleAddFile}
            handleRemoveFile={handleRemoveFile}
            isMobile={isMobile}
            isReadOnly={isReadOnly}
          />
          {/* Display existing complaint evidence */}
          {isReadOnly && (fileUrls.length > 0 || fileAliases.length > 0) && (
            <Box sx={{ mb: "16px" }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "black",
                  mb: "8px",
                  textTransform: "uppercase",
                }}
              >
                Complaint Evidence
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  p: "12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                }}
              >
                {fileUrls.map((url, index) => (
                  <IconButton
                    key={`url-${index}`}
                    onClick={() => handleOpenFile(url)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                        borderColor: "#999",
                      },
                      width: "fit-content",
                    }}
                    title={`Open file ${index + 1}`}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: "24px", color: "#666" }} />
                    <Typography sx={{ fontSize: "10px", color: "#666" }}>File {index + 1}</Typography>
                  </IconButton>
                ))}
                {fileAliases.map((alias, index) => (
                  <IconButton
                    key={`alias-${index}`}
                    onClick={() => handleOpenFile(fileUrls[index] || "#")}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                        borderColor: "#999",
                      },
                      width: "fit-content",
                    }}
                    title={`Open ${alias}`}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: "24px", color: "#666" }} />
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "#666",
                        maxWidth: "60px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {alias}
                    </Typography>
                  </IconButton>
                ))}
              </Box>
            </Box>
          )}
          {error && (
            <Typography fontSize="14px" margin="12px 0" style={{ color: "#d32f2f" }}>
              {error}
            </Typography>
          )}

          <ButtonContainer>
            {isReadOnly ? (
              <Box sx={{ display: "flex", gap: "12px", width: "100%", flexDirection: isMobile ? "column" : "row" }}>
                <RejectButton
                  style={{ flex: 1 }}
                  onClick={() => {
                    onConfirm({
                      decision: "reject",
                    });
                  }}
                  disabled={loading}
                  fullWidth={isMobile}
                >
                  {loading ? "Loading..." : "Reject"}
                </RejectButton>
                <AcceptButton
                  style={{ flex: 1 }}
                  onClick={() => {
                    onConfirm({
                      decision: "accept",
                    });
                  }}
                  disabled={loading}
                  fullWidth={isMobile}
                >
                  {loading ? "Loading..." : "Accept"}
                </AcceptButton>
              </Box>
            ) : (
              <ActionButton
                style={{ width: "100%" }}
                onClick={handleSubmit}
                disabled={loading || !isFormValid}
                fullWidth={isMobile}
              >
                {loading ? "Loading..." : confirmLabel}
              </ActionButton>
            )}
          </ButtonContainer>
        </MainContent>
      </ContentSection>
    </PopupContainer>
  );

  if (isMobile) {
    return (
      <StyledDrawer
        anchor="bottom"
        open={open}
        onClose={handleClose}
        onOpen={handleDrawerOpen}
        disableSwipeToOpen={true}
        swipeAreaWidth={0}
        {...sheetTransitionProps}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
      >
        {modalContent}
      </StyledDrawer>
    );
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      // Same open/close as every other popup (components/ScaleTransition).
      {...modalTransitionProps}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      }}
    >
      {modalContent}
    </StyledDialog>
  );
};

const StyledDrawer = styled(SwipeableDrawer)(() => ({
  "& .MuiDrawer-paper": {
    background: "transparent",
    borderRadius: "18px 18px 0 0",
    border: "none",
    boxShadow: "0 -4px 24px rgba(0, 0, 0, 0.3)",
    overflow: "hidden",
    maxHeight: "90vh",
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "transparent",
    borderRadius: 18,
    border: "none",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      margin: 12,
      maxWidth: 400,
      width: "auto",
      height: "auto",
      maxHeight: "90vh",
      scale: 1,
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: 480,
      height: "auto",
      width: 480,
    },
  },
}));

const PopupContainer = styled(Box)<{ isMobile: boolean }>(({ isMobile }) => ({
  position: "relative",
  width: isMobile ? "auto" : 480,
  height: "auto",
  display: "flex",
  flexDirection: "column",
}));

const ContentSection = styled(Box)<{ isMobile: boolean }>(({ isMobile }) => ({
  minHeight: isMobile ? 288 : "auto",
  maxHeight: isMobile ? "calc(90vh - 40px)" : "80vh",
  backgroundColor: "white",
  borderRadius: isMobile ? "18px 18px 0 0" : "18px",
  padding: isMobile ? "20px 28px 28px 28px" : "30px",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
}));

const MainContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "space-between",
});

const ButtonContainer = styled(Box)({
  marginTop: 20,
});

const ActionButton = styled(Button)<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  width: fullWidth ? "100%" : 262,
  height: 40,
  backgroundColor: "#c78154",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  border: "none",
  fontWeight: 400,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#c78154",
    border: "none",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    color: "#666",
    border: "none",
  },
}));

const AcceptButton = styled(Button)<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  width: fullWidth ? "100%" : "auto",
  height: 40,
  backgroundColor: "#000000",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  border: "none",
  fontWeight: 400,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#333333",
    border: "none",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    color: "#666",
    border: "none",
  },
}));

const RejectButton = styled(Button)<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  width: fullWidth ? "100%" : "auto",
  height: 40,
  backgroundColor: "#000000",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  border: "none",
  fontWeight: 400,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#333333",
    border: "none",
  },
  "&:disabled": {
    backgroundColor: "#ccc",
    color: "#666",
    border: "none",
  },
}));

export default ComplaintModal;
